import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Button from '@repo/ui/button'
import InnerBox from '@repo/ui/inner-box'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { getTripFolder } from '../../../api/trip-folder/get-trip-folder-details'
import { updateTripFolder } from '../../../api/trip-folder/update-trip-folder'
import { TripFolderInstructionInput } from '../../../lib/types/models/trip-folder/trip-folder-input'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledTextEditor from '../../elements/inputs/controlled-text-editor'

interface TripFolderInstructionsSectionProps {
  folderId: number
  defaultValues?: TripFolderInstructionInput
}

const tripFolderInstructionSchema = z.object({
  instruction: createStringSchema('Instruction', { nullish: true })
})

const TripFolderInstructionsSection = ({
  folderId,
  defaultValues
}: TripFolderInstructionsSectionProps) => {
  const { data: tripFolder } = useQuery({
    queryKey: ['trip-folder', folderId],
    queryFn: () => getTripFolder(folderId)
  })

  const form = useForm<TripFolderInstructionInput>({
    resolver: zodResolver(tripFolderInstructionSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { mutate, isPending } = useMutation({
    mutationFn: updateTripFolder,
    onSuccess: () => {
      toast.success('Instructions enregistrées avec succès')
    },
    onError: showAxiosError
  })

  const handleSubmit = form.handleSubmit((data) => {
    mutate({ id: folderId, ...data })
  })

  useEffect(() => {
    if (tripFolder) {
      form.reset({
        ...defaultValues,
        instruction: defaultValues?.instruction ?? tripFolder.instruction ?? ''
      })
    }
  }, [tripFolder, defaultValues])

  return (
    <InnerBox
      title="Instructions"
      titleColor={tailwindColors.grey.DEFAULT}
      className="rounded-2xl"
    >
      <Form {...form}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name={'instruction'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledTextEditor {...field} placeholder={'Instruction'} />
                </FormControl>
              </FormItem>
            )}
          />
          <Button
            type="submit"
            variant="ghost"
            className="w-fit self-end"
            label="Enregistrer les instructions"
            loading={isPending}
          />
        </form>
      </Form>
    </InnerBox>
  )
}

export default TripFolderInstructionsSection
